import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Package, Boxes, Sparkles, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, EASE } from "../../lib/motion";

const FEATURES = [
  {
    icon: TrendingUp,
    title: "Sales Intelligence",
    desc: "See what&apos;s selling, when, and why — across every category and season.",
  },
  {
    icon: Package,
    title: "Product Intelligence",
    desc: "Understand which styles win, which lag, and what to double down on.",
  },
  {
    icon: Boxes,
    title: "Inventory Intelligence",
    desc: "Know what to restock and what to clear before it ties up your cash.",
  },
  {
    icon: Sparkles,
    title: "AI Assistant",
    desc: "Just ask. Get plain-English answers about your business, instantly.",
  },
];

const Solution = () => {
  return (
    <section
      id="services"
      data-testid="solution-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#F0EBE1]/40 border-y border-[rgba(28,28,30,0.06)] overflow-hidden"
    >
      {/* blurred, friendly consulting backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1600&q=70"
          alt=""
          aria-hidden="true"
          className="jsc-drift w-full h-full object-cover opacity-[0.10] blur-[3px]"
        />
        <div className="absolute inset-0 bg-[#F0EBE1]/55" />
      </div>

      <div id="foresightflow" className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4">
              02 / The Solution
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-semibold text-[#1C1C1E] text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Meet <span className="italic text-[#1E3A5F]">ForesightFlow</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 font-body text-lg md:text-xl leading-relaxed text-[#3E4A5C]">
              One intelligence platform, built for the way fashion retail actually works.
              Four ways of seeing your business clearly.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[rgba(28,28,30,0.1)] border border-[rgba(28,28,30,0.1)]">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                data-testid={`feature-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.1 }}
                className="group relative bg-[#FAF8F5] p-8 lg:p-10 transition-colors duration-500 hover:bg-[#FAF8F5]/60"
              >
                <Icon className="w-8 h-8 text-[#3E4A5C] group-hover:text-[#B39B77] transition-colors duration-300" strokeWidth={1.4} />
                <h3 className="mt-8 font-display text-2xl text-[#1C1C1E]">{f.title}</h3>
                <p
                  className="mt-3 font-body text-sm leading-relaxed text-[#3E4A5C] min-h-[3rem]"
                  dangerouslySetInnerHTML={{ __html: f.desc }}
                />
                <Link
                  to="/foresightflow"
                  data-testid={`feature-learn-more-${i}`}
                  className="mt-8 inline-flex items-center gap-1.5 font-body text-sm font-medium text-[#1C1C1E] link-underline"
                >
                  Learn more
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Solution;
